import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, FileText, Package, Box } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingSheetsPage = () => {
  const [sheets, setSheets] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sheet: null });
  const [form, setForm] = useState({
    name: '',
    description: '',
    product_ids: [],
    package_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sheetsRes, productsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/pricing-sheets`),
        axios.get(`${API}/products`),
        axios.get(`${API}/packages`)
      ]);
      setSheets(sheetsRes.data);
      setProducts(productsRes.data.filter(p => p.active));
      setPackages(packagesRes.data.filter(p => p.active));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      product_ids: [],
      package_ids: []
    });
    setEditingSheet(null);
  };

  const openEditDialog = (sheet) => {
    setEditingSheet(sheet);
    setForm({
      name: sheet.name,
      description: sheet.description || '',
      product_ids: sheet.product_ids || [],
      package_ids: sheet.package_ids || []
    });
    setShowDialog(true);
  };

  const toggleProduct = (productId) => {
    setForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const togglePackage = (packageId) => {
    setForm(prev => ({
      ...prev,
      package_ids: prev.package_ids.includes(packageId)
        ? prev.package_ids.filter(id => id !== packageId)
        : [...prev.package_ids, packageId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name) {
      toast.error('Please enter a name');
      return;
    }

    if (form.product_ids.length === 0 && form.package_ids.length === 0) {
      toast.error('Please select at least one product or package');
      return;
    }

    try {
      if (editingSheet) {
        await axios.put(`${API}/pricing-sheets/${editingSheet.id}`, form);
        toast.success('Pricing sheet updated');
      } else {
        await axios.post(`${API}/pricing-sheets`, form);
        toast.success('Pricing sheet created');
      }

      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing sheet');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.sheet) return;
    try {
      await axios.delete(`${API}/pricing-sheets/${deleteDialog.sheet.id}`);
      toast.success('Pricing sheet deleted');
      setDeleteDialog({ open: false, sheet: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pricing sheet');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const getProductNames = (productIds) => {
    return productIds.map(id => products.find(p => p.id === id)?.name).filter(Boolean);
  };

  const getPackageNames = (packageIds) => {
    return packageIds.map(id => packages.find(p => p.id === id)?.name).filter(Boolean);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Pricing Sheets</h1>
          <p className="text-slate-600 mt-1">Create product & package groups for different galleries</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700" 
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="new-pricing-sheet-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Pricing Sheet
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : sheets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sheets.map((sheet) => (
            <Card key={sheet.id} className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{sheet.name}</CardTitle>
                    {sheet.description && (
                      <p className="text-sm text-slate-500 mt-1">{sheet.description}</p>
                    )}
                  </div>
                  <FileText className="h-5 w-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {sheet.product_ids?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-sm text-slate-500 mb-1">
                        <Box className="h-3 w-3" />
                        Products ({sheet.product_ids.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {getProductNames(sheet.product_ids).slice(0, 3).map((name, i) => (
                          <Badge key={name || `product-${i}`} variant="outline" className="text-xs">{name}</Badge>
                        ))}
                        {sheet.product_ids.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{sheet.product_ids.length - 3} more</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  {sheet.package_ids?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-sm text-slate-500 mb-1">
                        <Package className="h-3 w-3" />
                        Packages ({sheet.package_ids.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {getPackageNames(sheet.package_ids).slice(0, 3).map((name, i) => (
                          <Badge key={name || `package-${i}`} variant="outline" className="text-xs">{name}</Badge>
                        ))}
                        {sheet.package_ids.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{sheet.package_ids.length - 3} more</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(sheet)}
                    data-testid={`edit-sheet-${sheet.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDeleteDialog({ open: true, sheet })}
                    data-testid={`delete-sheet-${sheet.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No pricing sheets yet</h3>
            <p className="text-slate-500 mb-6">Create pricing sheets to group products for different gallery types</p>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => { resetForm(); setShowDialog(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Pricing Sheet
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pricing Sheet Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSheet ? 'Edit Pricing Sheet' : 'New Pricing Sheet'}</DialogTitle>
            <DialogDescription>
              {editingSheet ? 'Update pricing sheet details' : 'Create a group of products and packages for galleries'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Sheet Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Sports Pricing, School Pricing"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="sheet-name-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="sheet-description-input"
              />
            </div>

            {/* Products Selection */}
            <div className="space-y-3">
              <Label>Products ({form.product_ids.length} selected)</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {products.length > 0 ? products.map((product) => (
                  <div 
                    key={product.id}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={form.product_ids.includes(product.id)}
                        onCheckedChange={() => toggleProduct(product.id)}
                        data-testid={`product-checkbox-${product.id}`}
                      />
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.category}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium">{formatCurrency(product.price)}</span>
                  </div>
                )) : (
                  <p className="text-sm text-slate-500 text-center py-4">No products available</p>
                )}
              </div>
            </div>

            {/* Packages Selection */}
            <div className="space-y-3">
              <Label>Packages ({form.package_ids.length} selected)</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {packages.length > 0 ? packages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={form.package_ids.includes(pkg.id)}
                        onCheckedChange={() => togglePackage(pkg.id)}
                        data-testid={`package-checkbox-${pkg.id}`}
                      />
                      <div>
                        <p className="font-medium text-sm">{pkg.name}</p>
                        <p className="text-xs text-slate-500">{pkg.event_type}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium">{formatCurrency(pkg.price)}</span>
                  </div>
                )) : (
                  <p className="text-sm text-slate-500 text-center py-4">No packages available</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-sheet-btn">
                {editingSheet ? 'Update' : 'Create'} Sheet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, sheet: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Pricing Sheet</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.sheet?.name}"? Galleries using this sheet will show all products.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, sheet: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingSheetsPage;
